import { useRef, useState } from 'react';
import { RoomApiError, roomFetch, setRoomToken } from './roomFetch';

/**
 * The second half of the email gate: enter the six-digit code, get a Room session.
 *
 * The visitor has already been told a code was sent to their (masked) address. On success this
 * stores the session token and calls `onVerified`; the parent swaps in the wizard.
 *
 * Everything the visitor can do wrong here has to fail SOFTLY — a mistyped digit, an expired code,
 * an exhausted challenge — because a client filling this out is not an attacker and should never see
 * a stack of red errors for fat-fingering one number. The server's five-attempt lock is the actual
 * defence; this form's job is to make the honest path painless.
 */
export function OtpForm({
  challengeId,
  emailHint,
  onVerified,
  onBack,
}: {
  challengeId: string;
  emailHint: string;
  onVerified: (token: string) => void;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (code.length !== 6) {
      setError('הזינו את הקוד בן שש הספרות מהמייל.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const { token } = await roomFetch<{ token: string }>('room/otp/verify', {
        body: { challengeId, code },
      });
      setRoomToken(token);
      onVerified(token);
    } catch (err) {
      // The server answers every failure identically (wrong / expired / used / exhausted), so the
      // client cannot tell them apart either — and shouldn't try to. One honest message, and the
      // visitor can ask for a fresh code via "back".
      const msg =
        err instanceof RoomApiError && err.status === 401
          ? 'הקוד שגוי או שפג תוקפו. בדקו שוב, או בקשו קוד חדש.'
          : err instanceof RoomApiError
            ? err.message
            : 'משהו השתבש. נסו שוב.';
      setError(msg);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="room-card">
      <h1 className="room-card-title">הזינו את קוד האימות</h1>
      <p className="room-card-body">
        שלחנו קוד בן שש ספרות לכתובת <bdi>{emailHint}</bdi>. הקוד תקף לעשר דקות.
      </p>

      <form className="room-form" onSubmit={submit} noValidate>
        <label className="room-field">
          <span className="room-field-label">קוד אימות</span>
          <input
            ref={inputRef}
            className="room-input room-input-code"
            // Numeric on mobile without forcing type=number (which brings spinners and locale quirks).
            inputMode="numeric"
            autoComplete="one-time-code"
            // LTR so the digits read left-to-right inside an otherwise RTL page — the code is not
            // Hebrew text. dir on the input only, so it does not disturb the surrounding layout.
            dir="ltr"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-invalid={error !== null}
            autoFocus
          />
        </label>

        {error && (
          <p className="room-error" role="alert">
            {error}
          </p>
        )}

        <div className="room-actions">
          <button type="submit" className="btn-primary" disabled={busy || code.length !== 6}>
            {busy ? 'בודק…' : 'כניסה'}
          </button>
          <button type="button" className="btn-ghost" onClick={onBack} disabled={busy}>
            חזרה
          </button>
        </div>
      </form>
    </section>
  );
}
